import { Badge } from "@eduviaos/ui-kit";
import type { StageSuggestion } from "@eduviaos/shared";

import type { SuggestionSelection } from "../hooks/useSuggestionSelection.js";
import type { SelectableNode } from "../lib/collectNodes.js";
import { AIRequirementCard } from "./AIRequirementCard.js";
import { DiffNodeRow } from "./DiffNodeRow.js";

/**
 * L2 — one proposed admission stage and its bundled requirements
 * (Phase F-finale-4 Slice 5).
 *
 * The stage is its own selectable node (the "create this stage" checkbox);
 * each requirement is an independently-selectable child row. Used standalone
 * for `target_kind=stage` and inside {@link AIFunnelDiff}'s accordion content
 * for `target_kind=funnel`.
 */
export interface AIStageDiffProps {
  stage: StageSuggestion;
  selection: SuggestionSelection;
  /**
   * Title/aria for the stage's own checkbox. Defaults to the stage label
   * (standalone L2). {@link AIFunnelDiff} overrides it with an action phrase
   * ("Include the X stage") so the label isn't duplicated with the accordion
   * trigger that already names the stage.
   */
  stageLabel?: string;
}

function fallbackNode(nodeId: string): SelectableNode {
  return { node_id: nodeId, isValid: true };
}

export function AIStageDiff({ stage, selection, stageLabel }: AIStageDiffProps) {
  const stageNode = selection.nodeById.get(stage.node_id) ?? fallbackNode(stage.node_id);
  const def = stage.stage;
  const title = stageLabel ?? def.label;

  return (
    <div className="space-y-1">
      <DiffNodeRow
        node={stageNode}
        checked={selection.isSelected(stage.node_id)}
        onToggle={() => selection.toggle(stage.node_id)}
        ariaLabel={title}
        title={
          <span className="flex items-center gap-2">
            {title}
            <Badge variant="outline">position {def.order}</Badge>
            {def.terminal_outcome ? (
              <Badge variant={def.terminal_outcome === "admitted" ? "success" : "danger"}>
                {def.terminal_outcome}
              </Badge>
            ) : null}
          </span>
        }
        reason={stage.reason}
      />
      {stage.requirements_for_stage.length > 0 ? (
        <div className="border-border ml-6 space-y-1 border-l pl-3">
          {stage.requirements_for_stage.map((req) => (
            <AIRequirementCard
              key={req.node_id}
              requirement={req}
              node={selection.nodeById.get(req.node_id) ?? fallbackNode(req.node_id)}
              checked={selection.isSelected(req.node_id)}
              onToggle={() => selection.toggle(req.node_id)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
